import { useQuery } from "@tanstack/react-query";

import { get, post } from "./client";
import type { ApiResponse } from "./deals";
import { isApiError } from "./errors";
import { queryKeys } from "./queryKeys";

export interface RaidItemsQueryParams {
  status?: string;
  raidType?: string;
  skip?: number;
  take?: number;
}

export interface CurrentRaidItemDto {
  raidItemId: number;
  sourceDocumentVersionId: number;
  documentId: number;
  sourceRaidId?: string | null;
  sourceWorksheet?: string | null;
  sourceRowNumber: number;
  raidType: string;
  title: string;
  sourceDecisionName?: string | null;
  description?: string | null;
  category?: string | null;
  workstreamText?: string | null;
  substreamText?: string | null;
  dealPhase?: string | null;
  owner?: string | null;
  status?: string | null;
  normalizedStatus?: string | null;
  inferredStatus?: string | null;
  riskReasonCodes?: string[] | null;
  primaryReasonCode?: string | null;
  riskMetrics?: {
    insufficientDataReasons?: string[] | null;
  } | null;
  priority?: string | null;
  importance?: string | null;
  level?: string | null;
  criticality?: string | null;
  comments?: string | null;
  confidentiality?: boolean | null;
  likelihood?: string | null;
  impact?: string | null;
  mitigation?: string | null;
  resolution?: string | null;
  raisedOn?: string | null;
  dueDate?: string | null;
  raiseToImo?: boolean | null;
  raiseToWorkstream?: boolean | null;
  whoToValidate?: string | null;
  lastUpdatedAt?: string | null;
}

/** Stable current RAID fields rendered beside opaque AI insight analysis. */
export interface RaidInsightItemDto {
  itemId: string;
  raidType: string;
  title: string;
  lastUpdatedAt?: string | null;
  workstream?: string | null;
  criticality?: string | null;
  status?: string | null;
  owner?: string | null;
  validator?: string | null;
  dueDate?: string | null;
}

export function getRaidInsightItems(itemIds: readonly string[], dealDataVersionId?: number | null) {
  return post<ApiResponse<RaidInsightItemDto[]>>("/raid/insight-items/lookup", {
    itemIds,
    dealDataVersionId: dealDataVersionId ?? null,
  });
}

export function useGetRaidInsightItems(
  itemIds: readonly string[],
  enabled = true,
  dealDataVersionId?: number | null
) {
  const normalizedItemIds = [...new Set(itemIds.map((itemId) => itemId.trim()).filter(Boolean))].sort();

  return useQuery({
    queryKey: ["api", "raid", "insight-items", dealDataVersionId ?? null, normalizedItemIds] as const,
    enabled: enabled && normalizedItemIds.length > 0 && (dealDataVersionId == null || dealDataVersionId > 0),
    queryFn: async () => {
      const response = await getRaidInsightItems(normalizedItemIds, dealDataVersionId);
      return response.data ?? [];
    },
  });
}

export function getRaidItems(params?: RaidItemsQueryParams) {
  return get<ApiResponse<CurrentRaidItemDto[]>>("/raid/items", { params });
}

export function useGetRaidItems(params?: RaidItemsQueryParams) {
  return useQuery({
    queryKey: queryKeys.api.raidItems(params),
    queryFn: async () => {
      try {
        const response = await getRaidItems(params);
        return response.data ?? [];
      } catch (err) {
        if (isApiError(err) && err.status === 404) {
          return [];
        }
        throw err;
      }
    },
  });
}

export function useGetRaidDecisionItems() {
  return useGetRaidItems({
    raidType: "decision",
  });
}
